export const ROUTES = {
    ROOT: '/',
    LOGIN: '/login',
    DASHBOARD: '/dashboard',
    DASHBOARD_HOURS: '/dashboard/hours',
    TODAY: '/today',
    CATEGORIES: '/categories',
    PRODUCTS: '/products',
    STORE_PRICING: '/store-pricing',
    CLIENTS: '/utilisateurs',
    PROMO_CAMPAIGN: '/promo_campaign',
    STORES: '/stores',
    TEST_CARTS: '/test-carts',
    ENTREPRISES: '/entreprises',
    ACCOUNTING: '/comptabilite',
    QUALITY: '/quality',
    NOTIFICATIONS: '/notifications',
    CARTS: '/paniers',
    KIOSKS_CONTROL: '/controle-paniers',
    CAMERAS: '/cameras',
    DAILY_SALES: '/daily-sales',
    SELLERS: '/vendeur',
    STORE_INFORMATION: '/boutiques',
    SENSORS: '/sensors',
} as const;

// Redirect to this route on login
export const DEFAULT_LOGGED_IN_ROUTE = ROUTES.DASHBOARD;

export const CATEGORIES_ROUTES = {
    ADD_BRAND: 'add_brand',
    ADD_CATEGORY: 'add_category',
    ADD_INTERNAL_CATEGORY: 'add_internal_category',
    UPDATE_BRAND: 'update_brand/:id',
    UPDATE_CATEGORY: 'update_category/:id',
    UPDATE_INTERNAL_CATEGORY: 'update_internal_category/:id',
} as const;

export const SELLERS_ROUTES = {
    ADD_SELLER: 'add_seller',
    UPDATE_SELLER: 'update_seller/:id',
    ADD_ESTABLISHMENT: 'add_establishment',
    UPDATE_ESTABLISHMENT: 'update_establishment/:id',
};

export const ROUTE_BUILDER = {
    updateBrand: (id: number) => `update_brand/${id}`,
    updateCategory: (id: number) => `update_category/${id}`,
    updateInternalCategory: (id: number) => `update_internal_category/${id}`,
    updateCampaign: (id: number) => `update_campaign/${id}`,

    productDetails: (id: number) => `/products/details/${id}`,

    // Update products
    updateProductFirstStep: (id: number) => `/products/update_product/${id}/first-step`,
    updateProductSecondStep: (id: number) => `/products/update_product/${id}/second-step`,
    updateProductThirdStep: (id: number) => `/products/update_product/${id}/third-step`,
    updateProductFourthStep: (id: number) => `/products/update_product/${id}/fourth-step`,
    updateProductFifthStep: (id: number) => `/products/update_product/${id}/fifth-step`,

    // update works concil
    updateWorksConcilFirstStep: (id: number) => `/entreprises/update_entreprise/${id}/first-step`,
    updateWorksConcilSecondStep: (id: number) => `/entreprises/update_entreprise/${id}/second-step`,

    // Update stores
    updateStore: (id: number) => `update_store/${id}`,
    storeDetails: (id: number) => `store_details/${id}`,

    // Update users
    updateUser: (id: number) => `update_user/${id}`,

    // Duplicate notification
    duplicateNotification: (id: number) => `create_notification/${id}`,

    // View notification
    viewNotification: (id: number) => `view_notification/${id}`,

    // Inventory details
    inventoryDetails: (id: number) => `inventaire/${id}`,

    // Lot details (encodes stable lot identifiers: lot number, ean, tracking, and dlc)
    // Note: blocked, comment, and label are excluded as they can change
    lotDetails: (lotNumber: string, ean: string, tracking: string, dlc: string) => {
        // Encode only the stable fields that uniquely identify a lot
        const lotData = JSON.stringify({ lotNumber, ean, tracking, dlc });
        // Handle Unicode characters (like é, à, etc.) by encoding to UTF-8 first
        // Then use base64url encoding (URL-safe): replace +/= with -_~ to avoid URL routing issues
        const lotId = btoa(
            encodeURIComponent(lotData).replace(/%([0-9A-F]{2})/g, (_, p1) =>
                String.fromCharCode(parseInt(p1, 16))
            )
        )
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '~');
        return `/quality/${lotId}`;
    },

    // Update seller
    updateSeller: (id: number) => `update_seller/${id}`,

    // Update establishment
    updateEstablishment: (id: number) => `update_establishment/${id}`,
    seeStoreDetails: (id: number) => `/stores/store_details/${id}`,

    // Update sensor
    updateSensor: (id: number) => `update_sensor/${id}`,

    // Update fridge
    updateFridge: (id: number) => `update_fridge/${id}`,
} as const;

export const PRODUCTS_ROUTES = {
    ADD_PRODUCT: 'add_product',
    UPDATE_PRODUCT: 'update_product/:id',
    PRODUCT_DETAILS: 'details/:id',

    // Add products
    ADD_PRODUCT_FIRST_STEP: '/products/add_product/first-step',
    ADD_PRODUCT_SECOND_STEP: '/products/add_product/second-step',
    ADD_PRODUCT_THIRD_STEP: '/products/add_product/third-step',
    ADD_PRODUCT_FOURTH_STEP: '/products/add_product/fourth-step',
    ADD_PRODUCT_FIFTH_STEP: '/products/add_product/fifth-step',

    // Update products
    UPDATE_PRODUCT_FIRST_STEP: '/products/update_product/:id/first-step',
    UPDATE_PRODUCT_SECOND_STEP: '/products/update_product/:id/second-step',
    UPDATE_PRODUCT_THIRD_STEP: '/products/update_product/:id/third-step',
    UPDATE_PRODUCT_FOURTH_STEP: '/products/update_product/:id/fourth-step',
    UPDATE_PRODUCT_FIFTH_STEP: '/products/update_product/:id/fifth-step',

    // Import products
    IMPORT_PRODUCTS: 'import_products',
} as const;

export const CAMPAIGNS_ROUTES = {
    ADD_CAMPAIGN: 'add_campaign',
    UPDATE_CAMPAIGN: 'update_campaign/:id',
};

export const STORES_ROUTES = {
    ADD_STORE: 'add_store',
    UPDATE_STORE: 'update_store/:id',
    STORE_DETAILS: 'store_details/:id',
};

export const SENSORS_ROUTES = {
    ADD_SENSOR: 'add_sensor',
    UPDATE_SENSOR: 'update_sensor/:id',
    ADD_FRIDGE: 'add_fridge',
    UPDATE_FRIDGE: 'update_fridge/:id',
};

export const ENTREPRISES_ROUTES = {
    ADD_ENTREPRISE: 'add_entreprise',
    UPDATE_ENTREPRISE: 'update_entreprise/:id',

    ADD_ENTREPRISE_FIRST_STEP: '/entreprises/add_entreprise/first-step',
    ADD_ENTREPRISE_SECOND_STEP: '/entreprises/add_entreprise/second-step',

    UPDATE_ENTREPRISE_FIRST_STEP: '/entreprises/update_entreprise/:id/first-step',
    UPDATE_ENTREPRISE_SECOND_STEP: '/entreprises/update_entreprise/:id/second-step',
    ENTREPRISE_DETAILS: 'entreprise_details/:id',
};

export const USERS_ROUTES = {
    ADD_USER: '/utilisateurs/add_user',
    UPDATE: '/utilisateurs/update_user/:id',
};

export const NOTIFICATIONS_ROUTES = {
    NOTIFICATION: '/notifications',
    CREATE_NOTIFICATION: 'create_notification',
    DUPLICATE_NOTIFICATION: 'create_notification/:id',
    VIEW_NOTIFICATION: 'view_notification/:id',
};

export const CAMERAS_ROUTES = {
    CAMERAS: '/cameras',
};

export const INVENTORIES_ROUTES = {
    INVENTORY_DETAILS: 'store_details/:storeId/inventaire/:id',
};

export const DAILY_SALES_ROUTES = {
    DAILY_SALES: 'daily_sales',
    IMPORT: 'daily_sales/import',
};

export const BOUTIQUES_ROUTES = {
    BOUTIQUES: '/boutiques',
};
